import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Card, CardHeader, CardContent, Grid, Button, Typography, Box, CircularProgress, Switch } from '@material-ui/core';
import { ArrowDropDown, ArrowDropUp } from '@material-ui/icons';

import { Pagination } from '@material-ui/lab';
import usePagination from '../../../myHooks/usePagination';

import { rearrangeSurveyData, formatTimestampToDate } from '../../../helperFunctions';
import { getClientsWithExtractedFields } from '../../../helperFunctions/getSurveysWithExtractedFields';
import useViewportWidth from '../../../myHooks/useViewportWidth';

const useStyles = makeStyles(theme => ({
  card: {
    backgroundColor: 'white',
    boxShadow: '0px 2px 2px 1px #B4B4B4',
    borderRadius: '5px',
    flex: '1',
    marginBottom: '20px',

    '@media (max-width: 768px)': {
      '& .MuiGrid-root.MuiGrid-container': {
        flexWrap: 'nowrap !important',
      },
    },
  },
  cardHeader: {
    backgroundColor: '#E6F3F3',
    paddingBottom: 5,
  },
  heading: {
    fontWeight: 'bold',
    margin: 0,
    padding: '5px 0',
  },
  cardContent: {
    padding: 0,
  },
  gridContainer: {
    padding: '10px',
    borderBottom: '1px solid lightgray',
  },
  pagnationUlStyle: {
    '& .MuiPaginationItem-root': {
      '&.Mui-selected': {
        background: '#19989B',
        color: '#fff',
      },
    },
  },
  hover: {
    transition: '0.2s ease-in-out',
    '&:hover': {
      backgroundColor: '#E6F3F3',
      cursor: 'pointer',
    },
  },
  noDataContainer: {
    height: '350px',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  noDataText: {
    fontSize: '17px',
    margin: '0',
    padding: '0',
    textAlign: 'center',
    fontWeight: 400,
  },
  gridItemButtons: {
    '& button': {
      minWidth: '32px',
    },
  },
  switchRootActivated: {
    '& .MuiSwitch-track': {
      backgroundColor: '#82C2C3 !important',
    },
  },
  switchRootDeactivated: {
    '& .MuiSwitch-track': {
      backgroundColor: '#DADADA !important',
    },
  },
}));

const RecentCardExtended = ({
  heading,
  listSections,
  clients,
  activateClient,
  visibleAgencies,
  cardSize,
  isLoading,
  loadingAgencyData,
}) => {
  const classes = useStyles();
  const [filterActive, setFilterActive] = useState({});
  const [page, setPage] = useState(1);
  const viewportWidth = useViewportWidth();
  const [clientsState, setClientsState] = useState(clients || []);
  const [clientInProgress, setClientInProgress] = useState('');

  useEffect(() => {
    setClientsState(clients);
    setFilterActive({});
    handlePaginationChange(null, 1);
  }, [clients]);

  const handlePaginationChange = (e, p) => {
    setPage(p);
    _DATA.jump(p);
  };

  const truncateText = text => {
    if (viewportWidth > 768) {
      return text.length > 20 ? text.substring(0, 20).concat('...') : text;
    }

    if (text.length > 12) {
      return text.substring(0, 12).concat('...');
    }
    return text;
  };

  const handleFilterSelectChange = section => {
    const filterToUp = filterActive.section === section ? !filterActive.filterToUp : false;

    const filteredClients = rearrangeSurveyData(clientsState, section, filterToUp, 'clients');
    setClientsState(filteredClients);

    setFilterActive({
      section,
      filterToUp,
    });
  };

  const toggleClientHandler = (e, isClientActivated, agencyId) => {
    if (loadingAgencyData) return;

    setClientInProgress(e.currentTarget.id);
    activateClient(isClientActivated, agencyId);
  };

  const PER_PAGE = 10;
  const count = Math.ceil(clientsState.length / PER_PAGE);
  const _DATA = usePagination(clientsState, PER_PAGE);

  const dataExists = _DATA.currentData().length && !isLoading ? true : false;
  const clientsWithExtractedFields = getClientsWithExtractedFields(_DATA, dataExists, cardSize);

  return (
    <Box>
      <Card className={classes.card}>
        <CardHeader
          className={classes.cardHeader}
          title={
            <Grid container justifyContent="space-between" spacing={3}>
              <Grid item xs="auto">
                <Typography variant="h6" className={classes.heading}>
                  {heading}
                </Typography>
              </Grid>
            </Grid>
          }
        />
        <CardContent className={classes.cardContent}>
          <Grid
            container
            style={{
              padding: '10px',
              borderBottom: '1px solid lightgray',
            }}
            spacing={3}>
            {listSections.map((section, index) => {
              const isFirstElement = index === 0;
              const isFilterSelected = filterActive.section === section;
              const isOnOffSection = section === 'Turn on/off';
              return (
                <Grid
                  key={index}
                  item
                  sm={isFirstElement ? 2 : true}
                  style={{
                    paddingBottom: '10px',
                  }}
                  container
                  alignItems="center"
                  justifyContent={isFirstElement ? 'flex-start' : 'center'}>
                  {isOnOffSection ? (
                    <Typography variant="body2" style={{ fontSize: '13px', textTransform: 'none' }}>
                      {section}
                    </Typography>
                  ) : (
                    <Button
                      className={isFilterSelected ? 'recent-card-filter-active' : ''}
                      onClick={() => handleFilterSelectChange(section)}
                      disabled={!dataExists}>
                      <Typography variant="body2" style={{ fontSize: '13px', textTransform: 'none' }}>
                        {section}
                      </Typography>
                      {isFilterSelected && !filterActive.filterToUp ? <ArrowDropUp /> : <ArrowDropDown />}
                    </Button>
                  )}
                </Grid>
              );
            })}
          </Grid>
          {dataExists ? (
            <>
              {clientsWithExtractedFields.map((client, index) => {
                const isClientActivated = visibleAgencies.includes(client.agencyId);
                return (
                  <Grid
                    container
                    key={index}
                    className={`${classes.gridContainer} ${classes.hover}`}
                    justifyContent="space-between"
                    spacing={5}>
                    {Object.keys(client).map((clientField, secondIndex) => {
                      if (clientField === 'agencyId') return;

                      const isFirstElement = secondIndex === 1;
                      const isName = clientField === 'name';
                      const isDate = clientField === 'createdAt';

                      return (
                        <Grid
                          item
                          key={secondIndex}
                          sm={isName ? 2 : true}
                          container
                          alignItems="center"
                          justifyContent={isFirstElement ? 'flex-start' : 'center'}
                          style={{
                            paddingBottom: 0,
                            paddingLeft: isFirstElement ? '20px' : 0,
                          }}>
                          {isName ? (
                            <Typography variant="subtitle1">{truncateText(client[clientField])}</Typography>
                          ) : (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              style={{ fontSize: '12px', textAlign: isDate ? 'center' : 'left' }}>
                              {isDate
                                ? formatTimestampToDate(client[clientField])
                                : truncateText(String(client[clientField]))}
                            </Typography>
                          )}
                        </Grid>
                      );
                    })}
                    <Grid
                      item
                      container
                      sm
                      style={{ paddingBottom: '0', justifyContent: 'center' }}
                      wrap="nowrap"
                      className={classes.gridItemButtons}
                      direction="row">
                      <Box>
                        {loadingAgencyData && clientInProgress === client.agencyId ? (
                          <CircularProgress size={20} style={{ color: '#19989B', marginTop: '3px', marginBottom: '-3px' }} />
                        ) : (
                          <Switch
                            size="small"
                            color="secondary"
                            id={client.agencyId}
                            onChange={e => toggleClientHandler(e, isClientActivated, client.agencyId)}
                            checked={isClientActivated}
                            classes={{
                              root: isClientActivated ? classes.switchRootActivated : classes.switchRootDeactivated,
                            }}
                            style={{
                              color: isClientActivated ? '#19989B' : '#979797',
                            }}
                          />
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                );
              })}
            </>
          ) : (
            <Box className={classes.noDataContainer}>
              {isLoading ? (
                <CircularProgress size={40} style={{ color: '#19989B' }} />
              ) : (
                <Typography variant="h6" color="textSecondary" className={classes.noDataText}>
                  No clients found
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
      {_DATA.currentData().length ? (
        <Box display="flex" justifyContent="flex-end">
          <Pagination
            classes={{ ul: classes.pagnationUlStyle }}
            siblingCount={1}
            count={count}
            size="medium"
            page={page}
            variant="outlined"
            shape="rounded"
            onChange={handlePaginationChange}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default RecentCardExtended;
